import { Timestamp } from 'firebase/firestore'
import * as z from 'zod'

import { formatMoney } from '#formatting'
import { percentToRatio, ratioToPercent } from '#math/percentage'
import { Ownership } from '#profile/schemas'
import { zipCodeSchema } from '#schemas'
import type { Question } from '#survey'

import type { SubmissionAnswerValue } from '..'

const REQUIRED_MESSAGE = 'This field is required'

type SchemaBuilder<Q> = (question: Q) => z.ZodType<SubmissionAnswerValue>

const BUILDERS: {
  [Q in Question as Q['type']]: SchemaBuilder<Q>
} = {
  string: question =>
    question.isOptional
      ? z.string()
      : z.string().trim().min(1, 'This field is required'),

  integer: question => {
    let schema = z.int(REQUIRED_MESSAGE)
    if (question.min != null)
      schema = schema.min(question.min, `Must be at least ${question.min}`)
    if (question.max != null)
      schema = schema.max(question.max, `Must be no more than ${question.max}`)
    return schema
  },

  boolean: () => z.boolean(),

  date: () => z.instanceof(Timestamp, { error: 'Please select a date' }),

  zipCode: () => zipCodeSchema,

  money: question => {
    let schema = z.int(REQUIRED_MESSAGE)
    if (question.min != null)
      schema = schema.min(
        question.min,
        `Amount must be at least ${formatMoney(question.min)}`
      )
    if (question.max != null)
      schema = schema.max(
        question.max,
        `Amount must be no more than ${formatMoney(question.max)}`
      )
    return schema
  },

  // The value validates exactly like `money`. The companion `<id>Period` answer
  // gets its own schema in buildDefaultValuesAndSchema.
  moneyWithPeriod: question => {
    let schema = z.int(REQUIRED_MESSAGE)
    if (question.min != null)
      schema = schema.min(
        question.min,
        `Amount must be at least ${formatMoney(question.min)}`
      )
    if (question.max != null)
      schema = schema.max(
        question.max,
        `Amount must be no more than ${formatMoney(question.max)}`
      )
    return schema
  },

  percentage: question => {
    const min = question.min ?? -1000
    const max = question.max ?? 1000
    const innerSchema = z
      .float32(REQUIRED_MESSAGE)
      .min(min, `Percentage must be at least ${min}%`)
      .max(max, `Percentage must be no more than ${max}%`)
    // Stored as a ratio, validated as a percentage, so it has to travel both
    // ways. Doing that with bare arithmetic corrupts values that are clean on
    // arrival: 0.0615 in, 0.061500000000000006 out, and that is what reaches
    // Firestore. See ZL-1065 — this is the authoritative conversion, so a fix
    // in the form fields alone would not have been enough.
    return z
      .preprocess(
        v => (typeof v === 'number' ? ratioToPercent(v) : v),
        innerSchema
      )
      .transform(v => percentToRatio(v))
  },

  singleSelect: question =>
    z.enum(
      question.options.map(option =>
        typeof option === 'object' ? option.value : option
      ),
      'Please select an option'
    ),

  categorySelect: question =>
    z.enum(question.options, 'Please select a category'),

  owner: () => z.enum(Ownership),
}

export const buildAnswerSchema = (
  question: Question
): z.ZodType<SubmissionAnswerValue> => {
  const builder = BUILDERS[question.type] as SchemaBuilder<Question>

  let schema = builder(question)

  if (question.isOptional) {
    schema = schema.nullable()
  }

  return schema
}
